import { useEffect, useRef, useState } from 'react';

import { API_MAX_LIMIT } from '@/constants';
import { useWorkspaces } from '@/features/app/api/get-workspaces';
import { workspaceClientMap } from '@/features/app/utils/workspace-client-map';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { useFeaturedPeople } from '@/features/featured-people/api/get-featured-people';
import { useLogos } from '@/features/logos/api/get-logos';
import { useProgramsByParams } from '@/features/programs/api/get-programs-by-params';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';

import { rangeParams, TActiveOptionsSource } from './active-options';
import { useMentionFacets } from './use-mention-facets';

type TSelectionKey = 'broadcasters' | 'programs' | 'logos' | 'people' | 'workspaces' | 'tags';

type TPendingCheck = {
  /** The apply that started the check; a later one supersedes it. */
  applyCount: number;
  gte: string;
  lte: string;
  checked: Record<TSelectionKey, string[]>;
};

const SELECTION_KEYS: TSelectionKey[] = [
  'broadcasters',
  'programs',
  'logos',
  'people',
  'workspaces',
  'tags',
];

const ids = (items: { id: string }[] | undefined) => new Set((items ?? []).map(({ id }) => id));

/**
 * Drops, after the user applies a date range, the selected values that range makes inactive
 * in the given source's list.
 *
 * Triggered only by `rangeApplyCount` going up against the previous render: observing gte/lte
 * would fire on URL hydration, which runs in the /review layout after this page's effects,
 * and would prune a link's selection before the user touched anything.
 */
export function useDropInactiveFilterSelections(source: TActiveOptionsSource) {
  const { rangeApplyCount, gte, lte, setFilters } = useProgramBroadcastFilters();
  const lastSeenCount = useRef(rangeApplyCount);
  const [pending, setPending] = useState<TPendingCheck | null>(null);

  useEffect(() => {
    const increased = rangeApplyCount > lastSeenCount.current;
    lastSeenCount.current = rangeApplyCount;

    if (!increased) {
      setPending(null);
      return;
    }

    const state = useProgramBroadcastFilters.getState();
    setPending({
      applyCount: rangeApplyCount,
      gte,
      lte,
      checked: Object.fromEntries(SELECTION_KEYS.map(key => [key, [...state[key]]])) as Record<
        TSelectionKey,
        string[]
      >,
    });
  }, [rangeApplyCount]);

  const range = rangeParams({ source, gte: pending?.gte, lte: pending?.lte });
  const enabledFor = (key: TSelectionKey) => Boolean(pending?.checked[key].length);
  // Decides what to take away from the user, so it never answers from cache: the global
  // staleTime is Infinity, and the range may have gained rows since it was last asked.
  const checkConfig = (...keys: TSelectionKey[]) => ({
    enabled: keys.some(enabledFor),
    staleTime: 0,
  });

  const channels = useChannels({
    params: { idIn: pending?.checked.broadcasters, limit: API_MAX_LIMIT, ...range },
    config: checkConfig('broadcasters'),
  });
  const programs = useProgramsByParams({
    params: { idIn: pending?.checked.programs, limit: API_MAX_LIMIT, ...range },
    config: checkConfig('programs'),
  });
  const logos = useLogos({
    params: { idIn: pending?.checked.logos, limit: API_MAX_LIMIT, ...range },
    config: checkConfig('logos'),
  });
  const people = useFeaturedPeople({
    params: { idIn: pending?.checked.people, limit: API_MAX_LIMIT, ...range },
    config: checkConfig('people'),
  });
  const facets = useMentionFacets(
    { source, gte: pending?.gte, lte: pending?.lte },
    checkConfig('workspaces', 'tags')
  );
  const { data: workspaceCatalogue } = useWorkspaces();

  const checks = [
    { keys: ['broadcasters'], query: channels },
    { keys: ['programs'], query: programs },
    { keys: ['logos'], query: logos },
    { keys: ['people'], query: people },
    { keys: ['workspaces', 'tags'], query: facets },
  ] as const;

  const relevant = checks.filter(({ keys }) => keys.some(key => enabledFor(key)));
  const settled = relevant.every(({ query }) => !query.isFetching && !query.isPending);
  const failed = relevant.some(({ query }) => query.isError);

  useEffect(() => {
    if (!pending || !settled) return;
    // A newer range was applied in this same render, and its check is already queued: this
    // one's answer is about a range the user left, and clearing it would drop the new one.
    if (pending.applyCount !== useProgramBroadcastFilters.getState().rangeApplyCount) return;
    if (failed) {
      setPending(null);
      return;
    }

    const active: Record<TSelectionKey, Set<string>> = {
      broadcasters: ids(channels.data?.data),
      programs: ids(programs.data?.data),
      logos: ids(logos.data?.data),
      people: ids(people.data?.data),
      workspaces: new Set(facets.data?.workspaces ?? []),
      tags: new Set(facets.data?.tags ?? []),
    };

    const current = useProgramBroadcastFilters.getState();
    const next: Partial<Record<TSelectionKey | 'clients', string[]>> = {};

    SELECTION_KEYS.forEach(key => {
      const checked = new Set(pending.checked[key]);
      const inactive = (id: string) => checked.has(id) && !active[key].has(id);
      if (current[key].some(inactive)) {
        next[key] = current[key].filter(id => !inactive(id));
      }
    });

    if (next.workspaces) {
      const clientOf = workspaceClientMap(workspaceCatalogue ?? []);
      next.clients = Array.from(new Set(next.workspaces.map(id => clientOf[id]).filter(Boolean)));
    }

    if (Object.keys(next).length) setFilters(next);
    setPending(null);
  }, [pending, settled, failed]);
}
